import React, { useEffect, useMemo, useRef } from 'react'

import type { CompetitionMatch } from '../../../types/competition'

interface DeleteMatchModalProps {
  isOpen: boolean
  match: CompetitionMatch | null
  loading?: boolean
  error?: string | null
  onClose: () => void
  onConfirm: () => void
}

function formatDate(date: string | Date) {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export default function DeleteMatchModal({
  isOpen,
  match,
  loading = false,
  error = null,
  onClose,
  onConfirm,
}: DeleteMatchModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null)

  const title = useMemo(() => {
    if (!match) return 'Excluir luta'
    return match.opponent?.name
      ? `Excluir luta vs. ${match.opponent.name}?`
      : 'Excluir luta?'
  }, [match])

  useEffect(() => {
    if (!isOpen) return
    const id = window.setTimeout(() => cancelRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (!loading) onClose()
      }
      if (e.key === 'Enter') {
        // Confirm on Enter unless focused in a button that isn't delete
        const el = document.activeElement as HTMLElement | null
        const isButton = el?.tagName === 'BUTTON'
        const isCancel = el?.getAttribute('data-role') === 'cancel'
        if (!loading && (!isButton || !isCancel)) onConfirm()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, loading, onClose, onConfirm])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]"
      role="dialog"
      aria-modal="true"
      aria-label="Confirmação de exclusão"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose()
      }}
    >
      <div className="h-full w-full flex items-center justify-center px-4 py-10">
        <div className="bg-gray-50 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-white/10">
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-600 to-red-600 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.596c.75 1.334-.213 2.98-1.742 2.98H3.48c-1.53 0-2.492-1.646-1.743-2.98L8.257 3.1zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V7a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-extrabold text-white leading-tight">
                    {title}
                  </h2>
                  <p className="text-white/80 text-sm mt-1">
                    Essa ação não pode ser desfeita.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors disabled:opacity-50"
                aria-label="Fechar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            {match && (
              <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-gray-900 font-black tracking-tight">
                      {match.opponent?.name ? `vs. ${match.opponent.name}` : ''}
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      {match.eventName}
                    </div>
                    <div className="text-xs text-gray-400 font-semibold mt-1">
                      {formatDate(match.eventDate)}
                    </div>
                  </div>
                  {match.statistics?.points !== undefined && (
                    <div className="text-xs font-bold bg-gray-100 text-gray-700 px-3 py-2 rounded-xl">
                      {match.statistics.points}
                      {match.statistics.opponentPoints !== undefined
                        ? `-${match.statistics.opponentPoints}`
                        : ''}{' '}
                      pts
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <div className="flex gap-3">
              <button
                type="button"
                ref={cancelRef}
                data-role="cancel"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-5 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-semibold disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className="flex-1 px-5 py-3.5 bg-gradient-to-r from-rose-600 to-red-600 text-white rounded-xl hover:from-rose-700 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold shadow-lg shadow-rose-600/25"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                )}
                {loading ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
